import { withAITracking } from "@microsoft/applicationinsights-react-js";
import React from "react";
import { Container, Image } from "react-bootstrap";
import Row from "react-bootstrap/esm/Row";
import { reactPlugin } from "../../../utilities/AppInsights";
import "./WhiteLabelLoanPage.css"
import { AppState } from "redux/store/store";
import { toggleAPIRequestSuccess, toggleLoading } from "redux/actions/system/actions";
import Dealer from "models/dealerPurchase/dealer";
import { ConnectedProps, connect } from "react-redux";
import dealerService from "accessors/dealerService";
import LoadingSpinner from "shared/components/spinner/LoadingSpinner";
import LoanApplication from "models/loan/loanApplication";
import loanApplicationService from "accessors/loanApplicationService";
import ApplicantForm from "../../../components/loan/applicationForm/ApplicationForm";
import FullLogo from '../../../assets/full_logo.png'
import { LoanType } from "shared/Enums";
import PrimaryButton from "shared/components/PrimaryButton";
import { GlobalStyles } from "styles/styles";

const mapState = (state: AppState, route: any) => {
  const { system } = state;
  const { isLoading, apiRequestSuccess, loggedInAccount } = system;
  return {
    isLoading,
    apiRequestSuccess,
    route,
    loggedInAccount
  };
};

const mapDispatch = {
  toggleLoading: (isLoading: boolean) => toggleLoading(isLoading),
  toggleAPIRequestSuccess: (apiRequestSuccess: boolean) => toggleAPIRequestSuccess(apiRequestSuccess)
}

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

interface WhiteLabelLoanPageState {
  dealers: Dealer[];
  dealer: Dealer;
  loggedIn: boolean;
  sqId: string;
  defaultLoanType?: LoanType;
  loanTypesToShow: LoanType[];
  // For now, this is specific to one customer (BigIron) to move quickly
  //   Eventually, we will make this dynamic
  showAuctionButtons: boolean;
  anonDraftTokens: string[];
  dealerLoaded: boolean;
}

const isDev: boolean = window.location.host.includes('localhost') || window.location.host.includes('qa') || window.location.host.includes('uat');

class WhiteLabelLoanPage extends React.Component<Props, WhiteLabelLoanPageState> {
  constructor(props: any) {
    super(props);

    this.state = {
      dealers: [],
      dealer: {} as Dealer,
      loggedIn: Object.keys(this.props.loggedInAccount).length > 0,
      sqId: this.props.route.match.params.sqId,
      defaultLoanType: undefined,
      loanTypesToShow: [],
      showAuctionButtons: false,
      anonDraftTokens: [],
      dealerLoaded: false
    }
  }

  async componentDidMount() {
    await this.loadDealer();

    const oldToken = localStorage.getItem('agcredit_draft_token');
    if (oldToken) {
      localStorage.setItem('agcredit_draft_tokens', JSON.stringify([oldToken]));
      localStorage.removeItem('agcredit_draft_token');
    }
    const draftTokens = JSON.parse(localStorage.getItem('agcredit_draft_tokens') ?? '[]') as string[];

    if (this.state.loggedIn) {
      const params = new URLSearchParams(window.location.search);
      const claimTokensParam = params.get('claimTokens');
      const anonDraftTokens = claimTokensParam
        ? JSON.parse(decodeURIComponent(claimTokensParam)) as string[]
        : draftTokens;
      if (anonDraftTokens.length > 0) {
        this.setState({ anonDraftTokens });
        const url = new URL(window.location.href);
        url.searchParams.delete('claimTokens');
        window.history.replaceState({}, '', url.toString());
      }
    }
  }

  componentDidUpdate(prevProps: Props, prevState: Readonly<WhiteLabelLoanPageState>, snapshot?: any): void {
    if (prevProps.loggedInAccount !== this.props.loggedInAccount) {
      this.setState({ loggedIn: Object.keys(this.props.loggedInAccount).length > 0 });
    }
  }

  async loadDealer(): Promise<void> {
    this.props.toggleLoading(true);
    try {
      const dealer = await dealerService.GetDealerWhiteLabelInfo(this.state.sqId);
      if (Object.keys(dealer).length > 0) {
        let defaultLoanType: LoanType, loanTypesToShow: LoanType[], showAuctionButtons: boolean = false;

        if (dealer.dealerWhiteLabel.defaultLoanType != undefined) {
          defaultLoanType = dealer.dealerWhiteLabel.defaultLoanType;

          switch (defaultLoanType) {
            case LoanType.EquipmentAuctionPurchase:
              loanTypesToShow = [LoanType.EquipmentAuctionPurchase];
              showAuctionButtons = true;
              break;

            case LoanType.DealerPurchase:
            case LoanType.EquipmentRefinance:
              loanTypesToShow = [LoanType.DealerPurchase, LoanType.EquipmentRefinance];
              break;

            default:
              defaultLoanType = LoanType.DealerPurchase;
              loanTypesToShow = [LoanType.DealerPurchase, LoanType.EquipmentRefinance];
              showAuctionButtons = false;
              break;
          }
        } else {
          defaultLoanType = LoanType.DealerPurchase;
          loanTypesToShow = [LoanType.DealerPurchase, LoanType.EquipmentRefinance];
          showAuctionButtons = false;
        }

        const result = await dealerService.DownloadWhiteLabelLogo(dealer.dealerWhiteLabel.logoLocation);
        if (result.file) {
          dealer.dealerWhiteLabel.logoPreview = URL.createObjectURL(result.file);
          this.setState({ dealer, defaultLoanType, loanTypesToShow, showAuctionButtons, dealerLoaded: true });
        }
      }
    } finally {
      this.props.toggleLoading(false);
    }
  }

  async submitLoanApplication(loanApplication: LoanApplication): Promise<boolean> {
    this.props.toggleLoading(true);
    let loanId = 0;
    try {
      loanId = await loanApplicationService.SubmitLoanApplication(loanApplication);
    } finally {
      this.props.toggleLoading(false);
    }
    if (loanId > 0) {
      this.props.route.history.push(`/loan/${loanId}`);
    }
    return loanId > 0;
  }

  render() {
    if (!this.state.dealerLoaded) {
      return <LoadingSpinner variant="dark" animation="border" />;
    }

    return (
      <div className="container">
        <Container className="align-items-center">
          <Row className="justify-content-md-center">
            <Image
              src={this.state.dealer?.dealerWhiteLabel?.logoPreview}
              width="400px"
            />
          </Row>

          <Row className="justify-content-md-center">
            <h3>{this.state.dealer?.dealerWhiteLabel?.financingText}</h3>
          </Row>

          <Row className="justify-content-md-center">
            <div>
              <span>Powered by: <Image src={FullLogo} width={"100px"} /></span>
            </div>
          </Row>



          {this.state.showAuctionButtons &&
            <div style={{ marginTop: 20 }}>
              <Row className="justify-content-md-center">
                <div style={styles.auctionButtonHeaderText}>For <u>BigIron account registration</u>, please click here and register accordingly:</div>
              </Row>
              <Row className="justify-content-md-center">
                <PrimaryButton
                  buttonText="Account Registration"
                  onClick={() => window.location.href = isDev ? 'https://demo.bigiron.com/' : 'https://www.bigiron.com/'}
                  style={{ ...styles.auctionButton, ...{ backgroundColor: '#000' } }}
                />
              </Row>
              <div style={{ marginTop: 20 }}>
                <Row className="justify-content-md-center">
                  <div style={styles.auctionButtonHeaderText}>To get <u>pre-qualified for financing</u> before you bid, please click here:</div>
                </Row>
                <Row className="justify-content-md-center">
                  <PrimaryButton
                    buttonText="Apply For Financing"
                    onClick={() => this.setState({ showAuctionButtons: false })}
                    style={{ ...styles.auctionButton, ...{ backgroundColor: GlobalStyles.colors.primary } }}
                  />
                </Row>
              </div>
            </div>
          }

          {!this.state.showAuctionButtons &&
            <Row className="justify-content-md-center">
              <ApplicantForm
                customerAccountInfo={this.state.loggedIn ? this.props.loggedInAccount : undefined}
                userLoggedIn={this.state.loggedIn}
                dealers={[this.state.dealer]}
                defaultLoanType={this.state.defaultLoanType}
                loanTypesToShow={this.state.loanTypesToShow}
                defaultDealer={this.state.dealer}
                toggleLoading={this.props.toggleLoading}
                submitCallback={this.submitLoanApplication.bind(this)}
                anonymousDraftTokens={this.state.anonDraftTokens}
                sqId={this.state.sqId}
              ></ApplicantForm>
            </Row>
          }
        </Container>
        {this.props.isLoading &&
          <LoadingSpinner
            variant={'dark'}
            animation={'border'}
          />
        }
      </div>
    );
  }
}

const styles = {
  auctionButtonHeaderText:
  {
    fontSize: 36,
    fontStyle: 'italic',
    fontWeight: 'lighter'
  },
  auctionButton: {
    height: 100,
    width: 400,
    fontSize: 32,
    borderRadius: 20,
    marginTop: 10,
    marginBottom: 10
  }
};

export default withAITracking(reactPlugin, connector(WhiteLabelLoanPage));
